import React, { useEffect, useState } from "react";
import { db, ref } from "./firebase";
import { onValue, update, push, remove } from "firebase/database";
import { formatCourseId } from "./utils/formatCourse";

const ChaptersManagment = () => {
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  const [chapters, setChapters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newChapterName, setNewChapterName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  // Fetch courses
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsub = onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        setCourses(Object.keys(snapshot.val()));
      }
    });
    return () => unsub();
  }, []);

  // Fetch chapters for active course
  useEffect(() => {
    if (!activeCourse) return;
    const chaptersRef = ref(db, `courses/${activeCourse}/chapters`);
    const unsub = onValue(chaptersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const arr = Object.entries(data).map(([id, ch]) => ({
          id,
          name: ch.name || "",
          done: !!ch.done,
          completedAt: ch.completedAt || null
        }));
        setChapters(arr);
      } else {
        setChapters([]);
      }
    });
    return () => unsub();
  }, [activeCourse]);

  // Add chapter
  const handleAddChapter = async () => {
    if (!newChapterName.trim() || !activeCourse) return;
    await push(ref(db, `courses/${activeCourse}/chapters`), {
      name: newChapterName.trim(),
      done: false,
      completedAt: null
    });
    setNewChapterName("");
  };

  // Edit chapter
  const startEdit = (id, name) => {
    setEditingId(id);
    setEditingName(name);
  };
  const saveEdit = async () => {
    if (!editingName.trim() || !editingId) return;
    await update(ref(db, `courses/${activeCourse}/chapters/${editingId}`), {
      name: editingName.trim()
    });
    setEditingId(null);
    setEditingName("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  // Toggle done
  const toggleDone = async (id, done) => {
    await update(ref(db, `courses/${activeCourse}/chapters/${id}`), {
      done: !done,
      completedAt: !done ? new Date().toISOString() : null
    });
  };

  // Delete chapter
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this chapter?")) {
      await remove(ref(db, `courses/${activeCourse}/chapters/${id}`));
    }
  };

  // Filtered chapters
  const filteredChapters = chapters.filter(ch =>
    ch.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 text-gray-900 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Chapters</h1>

      {/* Course selector */}
      <div className="mb-6">
        <label htmlFor="chapters-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          id="chapters-course"
          value={activeCourse}
          onChange={(e) => setActiveCourse(e.target.value)}
          className="p-2 border rounded-md text-gray-900 min-w-[200px]"
        >
          <option value="">Select course</option>
          {courses.map((course) => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {!activeCourse ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
          <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
        </div>
      ) : (
        <>
      {/* Search and Add Chapter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <input
          type="text"
          placeholder="Search chapters..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="p-2 border rounded-md text-gray-900"
        />
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="New chapter name"
            value={newChapterName}
            onChange={e => setNewChapterName(e.target.value)}
            className="w-full p-2 border rounded-md text-gray-900"
          />
          <button
            onClick={handleAddChapter}
            className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700 whitespace-nowrap"
          >
            Add Chapter
          </button>
        </div>
      </div>

      {/* Chapters Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 font-semibold text-gray-900">
          <div className="col-span-6">Chapter Name</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-4">Actions</div>
        </div>
        {filteredChapters.length === 0 ? (
          <div className="p-6 text-center text-gray-600">
            No chapters found. Add your first chapter above.
          </div>
        ) : (
          filteredChapters.map(chapter => (
            <div
              key={chapter.id}
              className="grid grid-cols-12 gap-4 p-4 border-t border-gray-200 hover:bg-gray-50"
            >
              <div className="col-span-6 font-medium text-gray-900">
                {editingId === chapter.id ? (
                  <input
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    className="p-1 border rounded w-full"
                  />
                ) : (
                  chapter.name
                )}
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-1 rounded-full text-xs ${chapter.done ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                  {chapter.done ? "Completed" : "In Progress"}
                </span>
              </div>
              <div className="col-span-4 flex gap-2">
                {editingId === chapter.id ? (
                  <>
                    <button onClick={saveEdit} className="text-green-600 hover:text-green-800 font-medium">Save</button>
                    <button onClick={cancelEdit} className="text-gray-600 hover:text-gray-800 font-medium">Cancel</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => toggleDone(chapter.id, chapter.done)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {chapter.done ? "Mark Incomplete" : "Mark Complete"}
                    </button>
                    <button
                      onClick={() => startEdit(chapter.id, chapter.name)}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(chapter.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-4 text-right text-gray-600">
        Total chapters: {filteredChapters.length}
      </div>
        </>
      )}
    </div>
  );
};

export default ChaptersManagment;